<template>
  <div class="relative">
    <BaseMenu v-bind="$attrs" :hide-on-click="!multiple">
      <template #default="{ isVisible }">
        <BaseButton
          look="none"
          justify="start"
          class="hover:bg-ocean transition duration-150 ease-in-out !px-2"
          rounded="xl"
        >
          <div class="mr-1 sm:mr-3 font-normal">
            {{ title }}
          </div>
          <BaseIcon
            class="text-primary"
            name="outline_chevron_down"
            :class="isVisible && 'rotate-180'"
            size="md"
          />
        </BaseButton>
      </template>
      <template #menu>
        <div
          class="bg-white p-4 rounded-xl drop-shadow-lg mt-2 max-h-[350px] overflow-auto min-w-56"
        >
          <div
            class="flex gap-2"
            :class="[menuDirection === 'col' ? 'flex-col' : 'flex-wrap']"
          >
            <button
              v-for="(option, idx) in options"
              :key="idx"
              class="text-gray transition duration-150 ease-in-out flex items-center gap-2 rounded-xl"
              :class="getButtonClassList(option)"
              @click="onSelect(option)"
            >
              <div
                class="w-full truncate"
                :class="[menuDirection === 'col' ? 'text-left' : ' text-xs']"
              >
                {{ option.label || option.value }}
              </div>
            </button>
          </div>
        </div>
      </template>
    </BaseMenu>
  </div>
</template>

<script setup lang="ts">
import type { OptionInterface } from '~/components/base/controls/use-simple-input'

interface PropsInterface {
  modelValue: string | number | Array<string | number> | null | undefined
  options: OptionInterface[]
  title?: string
  multiple?: boolean
  menuDirection?: 'col' | 'row'
}

const props = withDefaults(defineProps<PropsInterface>(), {
  modelValue: undefined,
  title: '',
  multiple: false,
  options: () => [],
  menuDirection: 'col',
})

const emits = defineEmits(['update:modelValue'])

const parsedValue = computed(() => {
  if (props.multiple && !Array.isArray(props.modelValue)) {
    return [props.modelValue]
  }

  return props.modelValue
})

const selectedOptions = computed(() => {
  const value = parsedValue.value

  return props.options.filter((option) => {
    if (props.multiple && Array.isArray(value)) {
      return (
        value.includes(option.value) || value.includes(option.value?.toString())
      )
    }

    return option.value === value
  })
})

const selectedValues = computed(() =>
  selectedOptions.value.map(({ value }) => value)
)

function onSelect(option: OptionInterface) {
  select(option)

  if (!props.multiple) {
    close()
  }
}

function select(option: OptionInterface) {
  const value = parsedValue.value
  let result = option.value as any

  if (props.multiple && Array.isArray(value)) {
    if (selectedValues.value.includes(option.value)) {
      result = value.filter((v: any) => {
        if (parseInt(v)) {
          return parseInt(v) !== option.value
        }

        return v !== option.value
      })
    } else {
      result = [...value, option.value]
    }
  }

  emits('update:modelValue', result)
}

const getButtonClassList = (option: OptionInterface) => {
  if (selectedValues.value.includes(option.value)) {
    return props.menuDirection === 'col'
      ? 'py-2 px-4 bg-primary-100'
      : 'py-1 px-2 bg-primary-400'
  } else {
    return props.menuDirection === 'col'
      ? 'py-2 px-4 hover:bg-primary-100'
      : 'py-1 px-2 bg-primary-100 hover:bg-primary-400'
  }
}
</script>
